/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import type { CSSProperties, ReactNode, RefObject } from "react";
import { createRef, useMemo } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  fontSizes,
  fontWeights,
  lineHeights,
  radius,
  spacing,
  stroke,
} from "../../../../../global/emotion";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import { Skeleton } from "../Skeleton/Skeleton.emotion";
import { Text } from "../Text/Text.emotion";

const tableStyle: Interpolation = {
  borderCollapse: "collapse",
  width: spacing.full,
};
const headerStyle: Interpolation<ThemeType> = (theme) => ({
  background: theme.semanticColors.neutralsLowest,
  borderRadius: "none",
  borderWidth: stroke.none,
  color: theme.semanticColors.neutralsMedHigh,
  fontSize: fontSizes.titleS,
  fontWeight: fontWeights.titleS,
  lineHeight: lineHeights.titleS,
  ":first-of-type": { borderRadius: `${radius.s} 0 0 ${radius.s}` },
  ":last-of-type": { borderRadius: `0 ${radius.s} ${radius.s} 0` },
});
const cellStyle: Interpolation = {
  paddingBlock: spacing.ms,
  paddingLeft: spacing.m,
  ":first-of-type": { paddingLeft: spacing.l },
  paddingRight: spacing.none,
  ":last-of-type": { paddingRight: spacing.l },
  textAlign: "start",
};
const dataStyle: Interpolation<ThemeType> = (theme) => ({
  borderBlockWidth: stroke.light,
  borderColor: theme.semanticColors.neutralsLowest,
  borderInlineWidth: stroke.none,
  borderStyle: "solid",
});
const clickableStyle: Interpolation = {
  cursor: "pointer",
};
const skeletonStyle: Interpolation = {
  height: lineHeights.bodyS,
};
const hiddenStyle: Interpolation = {
  display: "none",
};
const columnStyle: Interpolation<string | number> = (width) => ({
  width,
});
const zebraStripingStyle: Interpolation<ThemeType> = (theme) => ({
  ":nth-of-type(even)": {
    background: `color-mix(in srgb, ${theme.semanticColors.neutralsLowest}, ${theme.semanticColors.secondary} 60%)`,
  },
});

type TableCellProps = HallowElementProps<"td"> & {
  clickable?: boolean;
};
const TableCell = ({ children, clickable, ...props }: TableCellProps) => (
  <Text
    asChild
    type="body"
    size="s"
    css={[cellStyle, dataStyle, !!clickable && clickableStyle]}
    {...props}
  >
    <td>{children}</td>
  </Text>
);

export type ColumnConfig<T extends any = any> = {
  label: ReactNode;
  order: number;
  render?: (value: any, row: T) => ReactNode;
  width?: CSSProperties["width"];
};

export type TableProps = HallowElementProps<"table"> & {
  columns: Record<string, ColumnConfig>;
  data: Array<{ [key in keyof TableProps["columns"]]: any }>;
  headerCellCSS?: Interpolation;
  dataCellCSS?: Interpolation;
  onRowClick?: (row: any) => void | Promise<void>;
  hasMore?: boolean;
  loadingSkeleton?: (column: keyof TableProps["columns"]) => ReactNode;
  infiniteScrollBufferRef?: RefObject<HTMLTableRowElement>;
  skeletonRows?: number;
  zebraStriping?: boolean;
};

export const Table = ({
  columns,
  data,
  headerCellCSS = {},
  dataCellCSS = {},
  onRowClick,
  hasMore,
  loadingSkeleton,
  infiniteScrollBufferRef = createRef<HTMLTableRowElement>(),
  skeletonRows = 1,
  zebraStriping = false,
  ...props
}: TableProps) => {
  const order = useMemo(
    () =>
      Object.keys(columns).sort((a, b) => columns[a].order - columns[b].order),
    [columns],
  );

  const skeletonArray = useMemo(() => {
    const arr = new Array(skeletonRows);
    for (let i = 0; i < arr.length; i++) {
      arr[i] = null;
    }
    return arr;
  }, [skeletonRows]);

  return (
    <table css={tableStyle} {...props}>
      <colgroup>
        {order.map((col) => (
          <col
            key={col}
            span={1}
            css={columnStyle(columns[col].width ?? "auto")}
          />
        ))}
      </colgroup>
      <thead>
        <tr>
          {order.map((column) => (
            <th key={column} css={[cellStyle, headerStyle, headerCellCSS]}>
              {columns[column].label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data?.map((r) => (
          <tr
            key={r?.id ?? JSON.stringify(r)}
            onClick={!!onRowClick ? () => onRowClick(r) : null}
            css={zebraStriping && zebraStripingStyle}
          >
            {order.map((column) => (
              <TableCell
                key={column}
                data-userid={r?.id}
                clickable={!!onRowClick}
                css={dataCellCSS}
              >
                {columns[column].render
                  ? columns[column].render(r[column], r)
                  : r[column]}
              </TableCell>
            ))}
          </tr>
        ))}
        <tr
          ref={infiniteScrollBufferRef}
          css={(!hasMore || !data) && hiddenStyle}
        >
          {order.map((column) => (
            <TableCell key={column} css={dataCellCSS}>
              {!!loadingSkeleton ? (
                loadingSkeleton(column)
              ) : (
                <Skeleton css={skeletonStyle} />
              )}
            </TableCell>
          ))}
        </tr>
        {!data?.length &&
          skeletonArray.map((_, idx) => (
            <tr key={`skeleton${idx}`}>
              {order.map((column) => (
                <TableCell key={column} css={dataCellCSS}>
                  {!!loadingSkeleton ? (
                    loadingSkeleton(column)
                  ) : (
                    <Skeleton css={skeletonStyle} />
                  )}
                </TableCell>
              ))}
            </tr>
          ))}
      </tbody>
    </table>
  );
};
